import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useAsync } from "react-use";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { Loading } from "../../../widgets/Loading";
import { formatDistanceToNow } from "date-fns";

type Props = {};

interface TailRequest {
  id: string;
  tail_number: string;
  requested_at: string;
  fbo: {
    name: string;
  };
  users: {
    email: string;
  };
}

/**
 * Tail Requests tab - shows pending tail number requests from users
 * Allows dismissing requests that have been handled or are not relevant.
 */
// Common styling for sticky table headers
const headerCellSx = {
  backgroundColor: "background.paper",
  fontWeight: 600,
  borderBottom: 2,
  borderColor: "divider",
};

export const TailRequestsTab: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const snackbar = useSnackbar();

  const { loading, value: requests } = useAsync(async () => {
    const { data, error } = await postgrest
      .from("tail_number_requests")
      .select("id,tail_number,requested_at,fbo(name),users(email)")
      .eq("status", "pending")
      .order("requested_at", { ascending: false });

    if (error) {
      console.error("Failed to fetch tail number requests:", error);
      return [];
    }

    return (data as TailRequest[]) || [];
  }, [postgrest]);

  const [dismissedIds, setDismissedIds] = React.useState<Set<string>>(
    new Set()
  );

  const handleDismiss = async (request: TailRequest) => {
    // Optimistic update - hide row immediately
    setDismissedIds((prev) => new Set(prev).add(request.id));

    try {
      const { error } = await postgrest
        .from("tail_number_requests")
        .update({ status: "dismissed" })
        .eq("id", request.id);

      if (error) throw error;

      snackbar.notify({
        text: t("screens.tailRequestsTab.dismissedMessage", {
          tailNumber: request.tail_number
        }),
        severity: "success",
      });
    } catch (error: any) {
      // Rollback on error - show row again
      setDismissedIds((prev) => {
        const next = new Set(prev);
        next.delete(request.id);
        return next;
      });

      console.error("Failed to dismiss request:", error);
      snackbar.notify({
        text: t("screens.tailRequestsTab.dismissError"),
        severity: "error",
      });
    }
  };

  // Filter out dismissed requests for optimistic UI
  const displayedRequests = React.useMemo(() => {
    if (!requests) return [];

    return requests.filter((request) => !dismissedIds.has(request.id));
  }, [requests, dismissedIds]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Loading />
      </Box>
    );
  }

  if (!displayedRequests || displayedRequests.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <Typography variant="body1" color="text.secondary">
          {t("screens.tailRequestsTab.emptyState")}
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer sx={{ maxHeight: "calc(100vh - 250px)" }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellSx}>{t("screens.tailRequestsTab.headers.tailNumber")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.tailRequestsTab.headers.fboName")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.tailRequestsTab.headers.requestedBy")}</TableCell>
            <TableCell sx={headerCellSx}>{t("screens.tailRequestsTab.headers.requestedAt")}</TableCell>
            <TableCell align="center" width={100} sx={headerCellSx}>
              {t("screens.tailRequestsTab.headers.actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {displayedRequests.map((request) => (
            <TableRow
              key={request.id}
              sx={{ "&:hover": { backgroundColor: "action.hover" } }}
            >
              <TableCell>
                <strong>{request.tail_number}</strong>
              </TableCell>
              <TableCell>{request.fbo?.name || t("screens.tailRequestsTab.unknown")}</TableCell>
              <TableCell>{request.users?.email || t("screens.tailRequestsTab.unknown")}</TableCell>
              <TableCell>
                {formatDistanceToNow(new Date(request.requested_at), {
                  addSuffix: true,
                })}
              </TableCell>
              <TableCell align="center">
                <Tooltip title={t("screens.tailRequestsTab.dismissTooltip")}>
                  <IconButton
                    size="small"
                    onClick={() => handleDismiss(request)}
                    color="error"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
